import { Component, EventEmitter, NgZone, OnDestroy, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, finalize, Subscription, take } from 'rxjs';
import { IdentityValidationAccessService } from '@shared/services/identity-validation-access/identity-validation-access.service';
import { SessionLogoutService } from '@shared/services/session-logout/session-logout.service';
import {
	IDENTITY_VALIDATION_NETWORK_ROUTE,
	IDENTITY_VALIDATION_PAYMENT_ROUTE,
	isIdentityValidationAllowedRoute
} from '@shared/guards/identity-validation-access/identity-validation-access.routes';
import { IdentityValidationModalComponent } from './identity-validation-modal.component';

@Component({
	selector: 'app-identity-validation-flow',
	standalone: true,
	imports: [IdentityValidationModalComponent],
	templateUrl: './identity-validation-flow.component.html'
})
export class IdentityValidationFlowComponent implements OnDestroy {
	@Output() onboardingReleased = new EventEmitter<void>();

	showIdentityValidationModal = false;
	identityModalClosable = false;

	private readonly subscriptions = new Subscription();
	private identityCheckPending = false;
	private identityCheckInProgress = false;
	private marketingFlyerFinished = true;
	private profileReady = false;
	private showLegacyModalAfterLogin = false;

	constructor(
		private readonly identityValidationAccess: IdentityValidationAccessService,
		private readonly sessionLogoutService: SessionLogoutService,
		private readonly router: Router,
		private readonly ngZone: NgZone
	) {
		this.profileReady = this.shouldCheckIdentityValidation(this.router.url);

		if (this.profileReady) {
			this.queueIdentityValidationCheck();
		}

		this.subscriptions.add(
			this.router.events
				.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
				.subscribe((event) => {
					this.profileReady = this.shouldCheckIdentityValidation(event.urlAfterRedirects);

					if (!this.profileReady) {
						this.showIdentityValidationModal = false;
						return;
					}

					this.queueIdentityValidationCheck();
				})
		);
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	onMarketingFlyerLoadStarted(): void {
		this.identityCheckPending = true;
		this.showLegacyModalAfterLogin = true;
		this.marketingFlyerFinished = false;
	}

	onMarketingFlyerLoadCompleted(hasFlyers: boolean): void {
		this.identityCheckPending = true;
		this.showLegacyModalAfterLogin = true;
		this.marketingFlyerFinished = !hasFlyers;
		this.tryLoadIdentityValidationStatus();
	}

	onMarketingFlyerClosed(): void {
		this.identityCheckPending = true;
		this.showLegacyModalAfterLogin = true;
		this.marketingFlyerFinished = true;
		this.tryLoadIdentityValidationStatus();
	}

	onIdentityModalOpenChange(open: boolean): void {
		this.showIdentityValidationModal = open;
		if (!open) this.releaseOnboardingGate();
	}

	goToPayment(): void {
		this.navigateToAllowedRoute(IDENTITY_VALIDATION_PAYMENT_ROUTE);
	}

	goToNetwork(): void {
		this.navigateToAllowedRoute(IDENTITY_VALIDATION_NETWORK_ROUTE);
	}

	private navigateToAllowedRoute(route: string): void {
		this.showIdentityValidationModal = false;
		this.identityCheckPending = false;
		// Payment/Network are allowed Identity routes, not a completed Identity flow.
		// Keep the membership gate paused until Identity is actually resolved.
		this.ngZone.run(() => {
			void this.router.navigateByUrl(route);
		});
	}

	logout(): void {
		this.showIdentityValidationModal = false;
		this.identityCheckPending = false;
		this.showLegacyModalAfterLogin = false;
		this.sessionLogoutService.logout();
	}

	private queueIdentityValidationCheck(): void {
		this.identityCheckPending = true;
		this.tryLoadIdentityValidationStatus();
	}

	private tryLoadIdentityValidationStatus(): void {
		if (
			!this.identityCheckPending ||
			this.identityCheckInProgress ||
			!this.marketingFlyerFinished ||
			!this.profileReady
		) {
			return;
		}

		this.identityCheckInProgress = true;
		this.subscriptions.add(
			this.identityValidationAccess
				.getStatus()
				.pipe(
					take(1),
					finalize(() => {
						this.identityCheckInProgress = false;
					})
				)
				.subscribe((accessState) => {
					this.identityCheckPending = false;

					if (!this.profileReady) {
						return;
					}

					if (!this.marketingFlyerFinished) {
						this.identityCheckPending = true;
						return;
					}

					const shouldOpenModal = accessState.blocked || (accessState.showModal && this.showLegacyModalAfterLogin);

					this.identityModalClosable = accessState.closable;
					this.showIdentityValidationModal = shouldOpenModal;

					if (!shouldOpenModal) {
						this.releaseOnboardingGate();
					}

					if (!accessState.blocked) {
						this.showLegacyModalAfterLogin = false;
					}
				})
		);
	}

	private releaseOnboardingGate(): void {
		this.identityValidationAccess.completeOnboardingGate();
		this.onboardingReleased.emit();
	}

	private isProfileUrl(url: string): boolean {
		return url.split(/[?#]/, 1)[0].startsWith('/profile/');
	}

	private shouldCheckIdentityValidation(url: string): boolean {
		return this.isProfileUrl(url) && !isIdentityValidationAllowedRoute(url);
	}
}
